import { useCallback, useState } from "react";

import { API_UPDATE_EMPLOYEE } from "../../../../../config/Endpoints.js";
import { getAuthToken } from "../../../../../utils/Storage.js";
import type {
  ContactStepValues,
  PersonalStepValues,
} from "../../create-employee/schemas/create-employee.schema.ts";
import { ApiError, postApi, putApi } from "../../../../utils/api.ts";
import { useSqeLabs } from "../../../../hooks/use-sqe-labs.ts";
import { SQE_ONBOARDING } from "../../onboarding/sqe/sqe-onboarding-api.ts";

function accessToken(): string | undefined {
  const t = getAuthToken("accessToken");
  return t != null && t !== "" ? t : undefined;
}

export type HrSetupUpdateEmployeePayload = PersonalStepValues &
  ContactStepValues & {
    id: string | number;
    usesUsIdentityFields?: boolean;
  };

function buildUpdatePayload(values: HrSetupUpdateEmployeePayload): Record<string, unknown> {
  const passportTrimmed = values.passportNumber?.trim() ?? "";
  return {
    id: values.id,
    ...(values.usesUsIdentityFields
      ? { ssn: values.ssn, zipCode: values.zipCode }
      : { aadharNum: values.aadharNumber, panNum: values.panCardNumber }),
    alternateNumber: values.alternateNumber,
    anniversaryDate: values.anniversaryDate?.trim() ?? "",
    ...(!values.usesUsIdentityFields
      ? {
          spouseName:
            values.maritalStatus === "Married" ? values.spouseName?.trim() ?? "" : "",
          spouseDob:
            values.maritalStatus === "Married" ? values.spouseDob?.trim() ?? "" : "",
          childName:
            values.maritalStatus === "Married" && values.hasChild === "yes"
              ? values.childName?.trim() ?? ""
              : "",
          childDob:
            values.maritalStatus === "Married" && values.hasChild === "yes"
              ? values.childDob?.trim() ?? ""
              : "",
          childGender:
            values.maritalStatus === "Married" && values.hasChild === "yes"
              ? values.childGender ?? ""
              : "",
        }
      : {}),
    bloodGroup: values.usesUsIdentityFields ? null : values.bloodGroup,
    dob: values.dob,
    doj: values.joiningDate,
    firstName: values.firstName,
    gender: values.gender,
    lastName: values.lastName,
    maritalStatus: values.maritalStatus,
    middleName: values.middleName?.trim() ?? "",
    passportNum: passportTrimmed === "" ? "" : passportTrimmed,
    permanentAddress: values.permanentAddress,
    personalEmail: values.personalEmail,
    phoneNumber: values.phoneNumber,
    presentAddress: values.presentAddress,
    alternateName: values.alternateName,
    relationWithAlternateNo: values.relationWithAlternateNo,
  };
}

export function useHrSetupUpdateEmployee() {
  const isSqe = useSqeLabs();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateEmployee = useCallback(async (values: HrSetupUpdateEmployeePayload) => {
    setLoading(true);
    setError(null);
    try {
      const url = isSqe ? `${SQE_ONBOARDING}/update` : API_UPDATE_EMPLOYEE;
      const payload = buildUpdatePayload(values);
      const { data } = isSqe
        ? await putApi<{ message?: string }>(url, accessToken(), payload)
        : await postApi<{ message?: string }>(url, accessToken(), payload);
      return data;
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Failed to update employee.";
      setError(msg);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [isSqe]);

  return { updateEmployee, loading, error };
}
